import { useMemo } from 'react';
import type { RecallAssignmentInfo } from '@/hooks/useRecallAssignmentStatus';
import type { DealerAssignmentSummary } from '../types/map.types';

/** Minimal recall shape this hook reads (key + workability inputs). */
interface RecallKeyParts {
  vin: string;
  campaign_number: string;
  has_advance_notice_recalls?: boolean;
}

/** Minimal dealer shape this hook reads/enriches (works for the map page's
 *  local DealerLocation and the shared map.types DealerLocation alike). */
interface DealerWithVehicles {
  dealerId: string;
  recallCount?: number;
  vehicles?: Array<{ recalls: RecallKeyParts[] }>;
}

interface SelectedRecallLike {
  vin?: string;
  campaign_number?: string;
}

/** Predicate: is this recall actually available to work right now? Advance-notice
 *  and paused recalls are excluded so they don't drag down "% handled". */
type IsWorkable = (recall: RecallKeyParts) => boolean;

/**
 * Build the status-map key the same way useRecallAssignmentStatus does:
 * `${vin}|${campaign_number}` with NO case normalization. The dealer dedup
 * upstream uppercases keys (`VIN-CAMPAIGN`); using that here would silently
 * miss every status row, so we must mirror createKey exactly.
 */
const statusKey = (vin: string, campaign: string): string => `${vin}|${campaign}`;

export interface DealerAssignmentSummariesResult<T> {
  /** Same dealers, each spread with an `assignmentSummary` field. */
  dealerLocationsWithSummary: Array<T & { assignmentSummary: DealerAssignmentSummary }>;
  /** Aggregate rollup across all dealers (for the legend / stats). */
  totalsSummary: {
    total: number;
    handled: number;
    unassigned: number;
    fullyHandledDealers: number;
  };
  /** Cheap version that changes whenever any dealer's handled count changes. */
  summaryVersion: number;
}

/**
 * Roll up a single dealer's recalls. Each recall is counted once:
 *  - handled    = in a route OR assigned to a technician OR staged for a route
 *  - unassigned = NOT handled AND available to work (advance-notice/paused excluded)
 * Non-workable, not-yet-handled recalls are intentionally ignored so the progress
 * ring reflects only actionable work.
 */
function summarizeDealer(
  dealer: DealerWithVehicles,
  statusMap: Map<string, RecallAssignmentInfo>,
  selectedKeySet: Set<string>,
  isWorkable: IsWorkable
): DealerAssignmentSummary {
  let total = 0;
  let handled = 0;
  let unassigned = 0;
  const byTechnician = new Map<string, number>();

  dealer.vehicles?.forEach(vehicle => {
    vehicle.recalls.forEach(recall => {
      total += 1;
      const key = statusKey(recall.vin, recall.campaign_number);
      const info = statusMap.get(key);

      const inRoute = !!info?.routeId;
      const assignedTech = !!info && info.status !== 'available' && !!info.technicianId;
      if (assignedTech && info?.technicianId) {
        byTechnician.set(info.technicianId, (byTechnician.get(info.technicianId) || 0) + 1);
      }

      if (inRoute || assignedTech || selectedKeySet.has(key)) {
        handled += 1; // actioned — counts toward progress regardless of workability
      } else if (isWorkable(recall)) {
        unassigned += 1; // available to work and not yet handled
      }
      // else: not handled and not workable (advance notice / paused) → ignored
    });
  });

  // Fall back to recallCount when vehicle detail isn't loaded yet, so the bubble
  // still reflects "nothing handled" rather than total 0 (assume workable).
  if (total === 0 && dealer.recallCount && dealer.recallCount > 0) {
    total = dealer.recallCount;
    unassigned = dealer.recallCount;
  }

  return {
    total,
    handled,
    unassigned,
    byTechnician,
    // "Fully handled" = there is actionable work and none of it is still waiting.
    fullyHandled: handled > 0 && unassigned === 0
  };
}

/**
 * Compute, for each dealer, how much of its actionable recall work is handled.
 * Single memoized pass over all recalls reading the status map directly.
 */
export function useDealerAssignmentSummaries<T extends DealerWithVehicles>(
  dealerLocations: T[],
  statusMap: Map<string, RecallAssignmentInfo>,
  selectedRecalls: SelectedRecallLike[],
  isWorkable: IsWorkable = () => true
): DealerAssignmentSummariesResult<T> {
  // Build the staged-selection lookup once per selectedRecalls change.
  const selectedKeySet = useMemo(() => {
    const set = new Set<string>();
    selectedRecalls.forEach(r => {
      if (r.vin && r.campaign_number) set.add(statusKey(r.vin, r.campaign_number));
    });
    return set;
  }, [selectedRecalls]);

  return useMemo(() => {
    const totals = { total: 0, handled: 0, unassigned: 0, fullyHandledDealers: 0 };

    const dealerLocationsWithSummary = dealerLocations.map(dealer => {
      const assignmentSummary = summarizeDealer(dealer, statusMap, selectedKeySet, isWorkable);
      totals.total += assignmentSummary.total;
      totals.handled += assignmentSummary.handled;
      totals.unassigned += assignmentSummary.unassigned;
      if (assignmentSummary.fullyHandled) totals.fullyHandledDealers += 1;
      return { ...dealer, assignmentSummary };
    });

    // Version changes whenever the handled/unassigned split shifts.
    const summaryVersion = totals.handled * 31 + totals.unassigned * 7;

    return { dealerLocationsWithSummary, totalsSummary: totals, summaryVersion };
  }, [dealerLocations, statusMap, selectedKeySet, isWorkable]);
}

export default useDealerAssignmentSummaries;
